﻿import React, { useMemo } from "react";
import "./EditPlugin.scss";
import * as coreApi from "../../shared/core-api-client";
import * as _ from "lodash";
import { Button, Dialog, DialogContent, TextField, DialogActions, DialogTitleWithCloseButton, Checkbox, InfoAlert, InlineInfoTooltip } from "../../shared/components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppActions } from "../../shared/hooks";
import { useSettings } from "../../shared/providers/SettingsProvider";
import { FormHelperText } from "@mui/material";
import { BranchMapping, BranchMappingEditor } from "./BranchMappingEditor";

const schema = z.object({
    name: z.string({ error: "This field is required." }).min(1, { error: "This field is required." }),
    description: z.string({ error: "This field is required." }),
    branchMappings: z.array(z.object({
        pattern: z.string().min(1, { error: "This field is required." }),
        url: z.string().optional(),
    })),
    isEnabled: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

interface EditPluginProps {
    open: boolean,
    setOpen: (open: boolean) => void,
    plugin?: coreApi.PluginListModel,
    onSuccess: () => void
}

function EditPlugin(props: EditPluginProps) {
    const { open, setOpen, plugin: _plugin, onSuccess } = props;
    const { showSuccess, showError } = useAppActions();
    const defaultValues: FormValues = { name: "", description: "", branchMappings: [{ pattern: "*", url: "" }], isEnabled: true };
    const { handleSubmit, reset, setError, control } = useForm({ resolver: zodResolver(schema), defaultValues });
    const [formErrors, setFormErrors] = React.useState(null);
    const [plugin, setPlugin] = React.useState<coreApi.PluginDisplayModel>(null);
    const settings = useSettings();
    const pluginsClient = useMemo(() => new coreApi.PluginsClient(settings.coreApiUrl), [settings.coreApiUrl]);

    React.useEffect(() => {
        if (_plugin) {
            pluginsClient.getById(_plugin.id)
                .then(result => {
                    setPlugin(result);
                    reset(result);
                });
        }
        else {
            reset(defaultValues);
        }
    }, [_plugin]);

    const closeDialog = () => {
        setOpen(false);
        reset(defaultValues);
    }

    const attachFormErrors = (errorDictionary: object) => {
        _.forOwn(errorDictionary, (value, key) => {
            if (key === "non_field_errors") {
                setFormErrors(value[0]);
            }
            else {
                setError(_.camelCase(key) as keyof FormValues, { type: "server", message: value[0] });
            }
        });
    }

    const save: SubmitHandler<FormValues> = (formModel) => {
        const model = {
            tenantId: settings.tenantId,
            ...plugin,
            ...formModel,
        };

        // If the user renamed an existing plugin, we need to delete the old one and create a new one...
        const apiCall = plugin && plugin.name !== formModel.name ?
            pluginsClient.deleteById(plugin.id).then(() => pluginsClient.upsert(model as coreApi.PluginInputModel)) :
            pluginsClient.upsert(model as coreApi.PluginInputModel);

        (apiCall as Promise<any>)
            .then(() => {
                closeDialog();
                showSuccess(`Plugin has been ${plugin ? "updated" : "created"} successfully.`);
                onSuccess();
            })
            .catch((ex: coreApi.ApiException) => {
                if (ex.status === 400) {
                    attachFormErrors((ex.result as coreApi.ValidationErrorResponse).errors ?? {});
                }
                else {
                    showError(ex.response);
                }
            });
    };

    return (
        <Dialog
            id="edit-plugin-dialog"
            open={open}
            onClose={(_event, reason) => {
                if (reason !== "backdropClick") {
                    closeDialog();
                }
            }}
            maxWidth="md"
            aria-labelledby="form-dialog-title">
            <form onSubmit={handleSubmit(save)}>
                <DialogTitleWithCloseButton onClose={closeDialog} id="form-dialog-title">
                    {`${plugin ? "Edit " + plugin.name : "Create Plugin"}`}
                </DialogTitleWithCloseButton>
                <DialogContent className="vertical-form padding-top-0">
                    <InfoAlert className="margin-bottom-2">
                        Additional components can be made available to the application via plugins.  Please ensure that the URL(s) specified below are accessible to the end user's web browser.
                    </InfoAlert>

                    <div className="vertical-form-field">Name</div>
                    <Controller
                        name="name"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                                autoFocus
                            />
                        )}
                    />

                    <div className="vertical-form-field">Description</div>
                    <Controller
                        name="description"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <div className="vertical-form-field">
                        URL Mapping Rules<InlineInfoTooltip text={
                            <ul>
                                <li>Evaluated in the order specified below (from top to bottom)</li>
                                <li>Wildcards are supported anywhere within the pattern field</li>
                                <li>The URL value of the first successful match is used</li>
                                <li>An empty URL value can be used to indicate that the plugin should not be loaded for that pattern</li>
                            </ul>
                        } />
                    </div>
                    <Controller
                        name="branchMappings"
                        control={control}
                        render={({
                            field: { value, onChange },
                        }) => (
                            <BranchMappingEditor
                                updateEntries={onChange}
                                entries={value as BranchMapping[]}
                            />
                        )}
                    />

                    <div className="vertical-form-field">Is Enabled?</div>
                    <Controller
                        name="isEnabled"
                        control={control}
                        render={({
                            field: { value, onChange },
                            fieldState: { error }
                        }) => (
                            <div className="d-flex flex-column">
                                <div>
                                    <Checkbox
                                        checked={value}
                                        onChange={onChange}
                                    />
                                </div>
                                <FormHelperText error={!!error}>{error?.message}</FormHelperText>
                            </div>
                        )}
                    />

                    {
                        formErrors &&
                        <div className="form-errors">
                            {formErrors}
                        </div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained" color="primary">
                        {`${plugin?.id ? "Update" : "Create"} Plugin`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default EditPlugin;